import React, { useState } from 'react'
import { DateTime } from 'luxon'
import { Card, CardBody, Collapse } from 'reactstrap'
import twas from 'twas'
import { ASSESSMENT_STATUS } from '../../../utils/common'

const TYPE_TO_TITLE = {
    INITIAL_SCAN: "Initial Scan Report",
    SECRET_SHOPPING: "Secret Shopping"
}

const STATUS_CONFIG = {
    [ASSESSMENT_STATUS.PENDING]:    { color: 'secondary', icon: 'fa-clock',               label: 'Pending' },
    [ASSESSMENT_STATUS.VERIFYING]:  { color: 'warning',   icon: 'fa-spinner fa-spin',      label: 'Waiting for Responses' },
    [ASSESSMENT_STATUS.VERIFIED]:   { color: 'info',      icon: 'fa-envelope-open-text',   label: 'Ready for Analysis' },
    [ASSESSMENT_STATUS.COMPLETE]:   { color: 'success',   icon: 'fa-check-circle',         label: 'Complete' },
    [ASSESSMENT_STATUS.ERROR]:      { color: 'danger',    icon: 'fa-exclamation-circle',   label: 'Error' },
}

function FranchescaAssessmentListItem({ assessment, onDownload, onChange, onSave, onEditStart, isLoading, draftAssessment }) {
    const { type, summary = {}, status } = assessment
    const [collapse, toggleCollapse] = useState(false)

    const { pass, warn, fail, indeterminate } = (assessment?.attr?.findings ?? [])?.reduce((prev, curr) => {
        prev[curr.status.toLowerCase()] += 1
        return prev
    }, {
        pass: 0,
        warn: 0,
        fail: 0,
        indeterminate: 0
    })

    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG[ASSESSMENT_STATUS.PENDING]

    return (
        <div className="list-group-item list-group-item-action p-3">
            <div className="d-flex align-items-center flex-wrap p-3">
                <div className="mr-auto pr-3">
                    <h4 className="d-flex align-items-center gap-2">
                        {TYPE_TO_TITLE[type]}
                        <span className={`badge badge-${cfg.color} badge-pill f-s-11 px-2 py-1 ml-2`}>
                            <i className={`fa ${cfg.icon} mr-1`} />
                            {cfg.label}
                        </span>
                    </h4>
                    <span className="text-muted f-s-12" title={DateTime.fromMillis(assessment?.timestamp).toLocaleString(DateTime.DATETIME_MED)}>
                        <i className="fa fa-calendar-alt mr-1" />
                        {twas(assessment?.timestamp, Date.now())}
                    </span>
                    {isLoading &&
                        <>
                            <span className="spinner-border spinner-border-sm ml-2" />
                        </>
                    }
                </div>
                {status === ASSESSMENT_STATUS.COMPLETE ? <div className="d-flex align-items-center mr-3">
                    <span className="badge badge-success badge-pill f-s-11 mr-1 px-2 py-1">
                        <i className="fa fa-check mr-1" />
                        {pass} Pass
                    </span>
                    <span className="badge badge-warning badge-pill f-s-11 mr-1 px-2 py-1">
                        <i className="fa fa-exclamation mr-1" />
                        {warn} Warn
                    </span>
                    <span className="badge badge-danger badge-pill f-s-11 px-2 py-1">
                        <i className="fa fa-times mr-1" />
                        {fail} Fail
                    </span>
                </div> : <></>}
                <div className="btn-group">
                    {(["SECRET_SHOPPING"].includes(assessment?.type) && status !== ASSESSMENT_STATUS.COMPLETE) && <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => {
                            if(!collapse){
                                onEditStart(assessment)
                            } else {
                                onEditStart({})
                            }
                            toggleCollapse(!collapse)
                        }}
                        disabled={isLoading}
                        title="Edit assessment"
                    >
                        <i class="far fa-edit"></i>
                    </button>}
                    <button
                        type="button"
                        className="btn btn-white"
                        onClick={() => onDownload?.(assessment)}
                        disabled={isLoading || status !== ASSESSMENT_STATUS.COMPLETE}
                        title="Download PDF"
                    >
                        <>
                            <i class="fas fa-download"></i>
                        </>
                    </button>
                </div>
            </div>
            <Collapse isOpen={collapse}>
                {assessment?.type === "SECRET_SHOPPING" && <>
                    <div className="row p-20">
                        <div className="col">                        
                            <div class="form-group">
                                <label>Form Submitted</label>
                                <input
                                    type="datetime-local"
                                    onChange={(e) => {
                                        let {name, value} = e.target
                                        onChange(name, DateTime.fromISO(value).toMillis())
                                    }}
                                    name="emailResults.emailReceivedAt"
                                    value={draftAssessment?.attr?.emailResults?.emailReceivedAt ? DateTime.fromMillis(draftAssessment?.attr?.emailResults?.emailReceivedAt).toFormat("yyyy-MM-dd'T'HH:mm") : ""} 
                                    class={`form-control form-control-plaintext`} disabled />
                                {/* <small id="emailHelp" class="form-text text-muted">We'll never share your email with anyone else.</small> */}
                            </div>
                            <div class="form-group">
                                <label>First Email Received</label>
                                <input
                                    type="datetime-local"
                                    onChange={(e) => {
                                        let {name, value} = e.target
                                        onChange(name, DateTime.fromISO(value).toMillis())
                                    }}
                                    name="formSubmissionResults.submittedAt"
                                    value={draftAssessment?.attr?.formSubmissionResults?.submittedAt ? DateTime.fromMillis(draftAssessment?.attr?.formSubmissionResults?.submittedAt).toFormat("yyyy-MM-dd'T'HH:mm") : ""} 
                                    class={`form-control form-control-plaintext`} disabled />
                            </div>
                        </div>
                        <div className="col">                        
                            <div class="form-group">
                                <label>First AI SMS Received</label>
                                <input
                                    type="datetime-local"
                                    onChange={(e) => {
                                        let {name, value} = e.target
                                        onChange(name, DateTime.fromISO(value).toMillis())
                                    }}
                                    name="smsResults.smsReceivedAt"
                                    value={draftAssessment?.attr?.smsResults?.smsReceivedAt ? DateTime.fromMillis(draftAssessment?.attr?.smsResults?.smsReceivedAt).toFormat("yyyy-MM-dd'T'HH:mm") : ""} 
                                    class={`form-control form-control-plaintext`} disabled />
                            </div>
                            <div class="form-group">
                                <label>First Open Appointment</label>
                                <input
                                    type="datetime-local"
                                    onChange={(e) => {
                                        let {name, value} = e.target
                                        onChange(name, DateTime.fromISO(value).toMillis())
                                    }}
                                    name="apptResults.bookingDate"
                                    value={draftAssessment?.attr?.apptResults?.bookingDate ? DateTime.fromMillis(draftAssessment?.attr?.apptResults?.bookingDate).toFormat("yyyy-MM-dd'T'HH:mm") : ""} 
                                    class={`form-control ${[ASSESSMENT_STATUS.COMPLETE, ASSESSMENT_STATUS.PENDING].includes(status) ? "form-control-plaintext" : ""}`} 
                                    {...([ASSESSMENT_STATUS.COMPLETE, ASSESSMENT_STATUS.PENDING].includes(status) ? ({disabled: true}) : {})}
                                />
                            </div>
                        </div>
                    </div>

                </>}
                <div className="d-flex flex-row-reverse">
                    <button className="btn btn-success" onClick={async() => {
                        toggleCollapse(!collapse)                        

                        await onSave()
                    }}>
                        Save
                    </button>
                </div>
            </Collapse>
        </div>
    );
}

export default FranchescaAssessmentListItem